import { useCallback, useState } from 'react'
import DocumentModel, { CreatePresignedUrlRequest, objectTypes } from '@/core/models/document.model'
import type { UploadedFileAttachment } from '@/core/models/medconnect-agent.model'

interface UseAgentChatUploadsArgs {
    onError?: (message: string) => void
    t: (key: string, vars?: any) => string
}

/** Extracts an HTTP status from axios-style or fetch-style errors, if any. */
function extractStatus(error: unknown): number | undefined {
    const anyErr = error as any
    return anyErr?.response?.status ?? anyErr?.status ?? anyErr?.data?.statusCode
}

/**
 * File attachments for the agent chat: presigned S3 upload + document registration on the session.
 *
 * The three stages (presign → S3 PUT → register) are each identified in the error message so a
 * failure on a remote environment (S3 CORS, proxy limits, expired token) is diagnosable from the
 * UI banner. Transient failures are retried once.
 */
export function useAgentChatUploads({ onError, t }: UseAgentChatUploadsArgs) {
    const [isUploading, setIsUploading] = useState(false)

    const uploadFile = useCallback(
        async (file: File, sessionId: string): Promise<UploadedFileAttachment | null> => {
            setIsUploading(true)
            let stage: 'presign' | 's3-put' | 'register' = 'presign'
            let lastError: unknown = null
            try {
                for (let attempt = 0; attempt < 2; attempt++) {
                    try {
                        stage = 'presign'
                        const urlRequest: CreatePresignedUrlRequest = {
                            fileName: file.name,
                            fileType: file.type,
                            objectType: objectTypes.AGENT_CHAT_SESSION,
                            objectId: sessionId,
                            sessionId,
                        }
                        const presigned = await DocumentModel.generatePresignedUrl(urlRequest)

                        stage = 's3-put'
                        await DocumentModel.uploadDocumentToAwsS3(presigned.url, file)

                        stage = 'register'
                        const documentDto = {
                            filename: presigned.fileName,
                            key: presigned.key,
                            objectType: objectTypes.AGENT_CHAT_SESSION,
                        }
                        const doc = await DocumentModel.addDocument(documentDto, objectTypes.AGENT_CHAT_SESSION, sessionId)
                        return { filename: doc.filename, key: doc.key, url: doc.url || '', objectType: doc.objectType as string }
                    } catch (error) {
                        lastError = error
                        console.error(`[useAgentChatUploads] Upload attempt ${attempt + 1} failed at stage "${stage}" for ${file.name}:`, error)
                        if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 750))
                    }
                }
                const status = extractStatus(lastError)
                const detail = `${stage}${status ? ` HTTP ${status}` : ''}: ${(lastError as Error)?.message ?? 'unknown error'}`
                onError?.(t('page.medconnectAgent.chatInterface.fileUploadFailed', { message: detail }))
                return null
            } finally {
                setIsUploading(false)
            }
        },
        [onError, t]
    )

    return { isUploading, uploadFile }
}

export default useAgentChatUploads
